import { z } from 'zod';
/**
 * Trace's custom access-token claims. Tokens carry them under a namespace URL (AUTH0_CLAIM_NAMESPACE in
 * platform-api, DEFAULT_CLAIM_NAMESPACE unless an environment sets another), so the organization id travels as
 * `https://trace.io/trace_org_id`, never as a bare `trace_org_id`.
 *
 * This is the one list of names: platform-api signs development tokens with it, the Auth0 claims Action
 * (auth/tenant, which runs on Auth0 and cannot import packages) is held to it by its tests, and product
 * backends read their own verified tokens with readTraceClaims.
 *
 * Organization-scoped product tokens carry trace_org_id (and trace_user_id), plus portal_id and
 * customer_account_id for a portal context. A product backend never trusts these alone: it passes them to
 * POST /v1/internal/access/check, which answers from current platform records for exactly that scope.
 */
export const DEFAULT_CLAIM_NAMESPACE = 'https://trace.io/';
export const TRACE_CLAIMS = {
    /** The identity-provider connection of the session; an organization that enforces SSO compares it. */
    connection: 'connection',
    /** The Trace organization (UUID) of the context: the organization, or the portal's provider organization. */
    organizationId: 'trace_org_id',
    /** The Trace user (UUID) of the person. */
    userId: 'trace_user_id',
    /** Portal contexts only: the portal (UUID). */
    portalId: 'portal_id',
    /** Portal contexts only: the customer account (UUID). */
    customerAccountId: 'customer_account_id',
};
/** The full claim name under a namespace, for example https://trace.io/trace_org_id. */
export function traceClaimName(claim, namespace = DEFAULT_CLAIM_NAMESPACE) {
    return `${namespace}${TRACE_CLAIMS[claim]}`;
}
/**
 * The Trace claims of one access token, keyed by TRACE_CLAIMS keys. Every claim is optional (a token outside
 * an organization carries none of the scope), but what is present must be whole: portal_id and
 * customer_account_id come together, and only next to the provider's trace_org_id.
 */
export const traceTokenClaimsSchema = z
    .object({
    connection: z.string().min(1).max(320).optional(),
    organizationId: z.uuid().optional(),
    userId: z.uuid().optional(),
    portalId: z.uuid().optional(),
    customerAccountId: z.uuid().optional(),
})
    .superRefine((claims, ctx) => {
    if ((claims.portalId === undefined) !== (claims.customerAccountId === undefined)) {
        ctx.addIssue({
            code: 'custom',
            path: ['portalId'],
            message: 'portal_id and customer_account_id come together',
        });
    }
    if (claims.portalId !== undefined && claims.organizationId === undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['organizationId'],
            message: 'a portal scope carries its provider organization (trace_org_id)',
        });
    }
});
/**
 * Reads the Trace claims of an access token payload the caller has ALREADY VERIFIED (signature, issuer,
 * audience, expiry). Only names under the namespace count. A claim that is present but malformed fails the
 * whole read, so a product never acts on a partial scope: refuse such a token.
 */
export function readTraceClaims(payload, namespace = DEFAULT_CLAIM_NAMESPACE) {
    const raw = {};
    for (const [key, name] of Object.entries(TRACE_CLAIMS)) {
        const value = payload[`${namespace}${name}`];
        if (value !== undefined)
            raw[key] = value;
    }
    return traceTokenClaimsSchema.safeParse(raw);
}
/**
 * The namespaced claims a token carries for these values: what development identity signs, under the names
 * the Auth0 claims Action writes. Throws when the values would not pass traceTokenClaimsSchema.
 */
export function traceClaimsPayload(claims, namespace = DEFAULT_CLAIM_NAMESPACE) {
    const checked = traceTokenClaimsSchema.parse(claims);
    const payload = {};
    for (const [key, name] of Object.entries(TRACE_CLAIMS)) {
        const value = checked[key];
        if (value !== undefined)
            payload[`${namespace}${name}`] = value;
    }
    return payload;
}
//# sourceMappingURL=claims.js.map